import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const links = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Leadership", href: "#leadership" },
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Clients", href: "#clients" },
  { label: "Contact", href: "#quote" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", href);
    }, 320);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-brutal ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <div className="w-9 h-9 bg-[#EA580C] flex items-center justify-center shadow-brutal transition-brutal group-hover:rotate-3">
              <span className="font-display font-black text-white text-lg leading-none">
                A
              </span>
            </div>
            <div className="leading-none">
              <div className="font-display font-black tracking-tighter text-slate-900 text-lg">
                SHREE AMBEY
              </div>
              <div className="font-display font-light tracking-[0.25em] text-slate-500 text-[10px] uppercase">
                Roadlines
              </div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-700 hover:text-[#EA580C] transition-brutal"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#quote"
              data-testid="nav-cta-quote"
              className="hidden sm:inline-flex items-center bg-[#EA580C] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 shadow-brutal hover-shadow-brutal transition-brutal"
            >
              Get a Quote
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-11 h-11 border-2 border-slate-900 flex items-center justify-center bg-white shadow-brutal"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goTo(e, l.href)}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="text-base font-bold uppercase tracking-wider text-slate-900 py-2 border-b border-slate-100"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#quote"
                onClick={(e) => goTo(e, "#quote")}
                data-testid="mobile-cta-quote"
                className="mt-2 inline-flex justify-center bg-[#EA580C] text-white font-bold uppercase tracking-wider text-sm px-5 py-4 shadow-brutal"
              >
                Get a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
